'use client';

import { cloneElement, type ReactElement, type ReactNode } from 'react';

import { type VariantProps } from 'tailwind-variants';

import { useImageLoadingStatus } from '../../internal/hooks/useImageLoadingStatus/useImageLoadingStatus';
import { type Component, type ComponentProps } from '../../types/component';
import { type ResponsiveOption } from '../../types/responsiveOption';
import { resolveResponsiveProps } from '../../utils/resolveResponsiveProps';
import { tv } from '../../utils/tv';
import { CoreIcon } from '../core-icon/CoreIcon';
import { Typography } from '../typography/Typography';

const avatarClasses = {
  root: 'zds-avatar-root',
  image: 'zds-avatar-image',
} as const;

const avatarStyles = tv({
  slots: {
    root: [
      avatarClasses.root,
      'box-border inline-flex shrink-0 items-center justify-center overflow-hidden align-middle leading-none select-none',
    ],
    image: [avatarClasses.image, 'h-full w-full object-cover'],
  },
  variants: {
    size: {
      sm: { root: 'z--avatar-sm-height z--avatar-sm-width' },
      md: { root: 'z--avatar-md-height z--avatar-md-width' },
      lg: { root: 'z--avatar-lg-height z--avatar-lg-width' },
      xl: { root: 'z--avatar-xl-height z--avatar-xl-width' },
    },
    sizeXs: {
      sm: { root: 'xs:z--avatar-sm-height xs:z--avatar-sm-width' },
      md: { root: 'xs:z--avatar-md-height xs:z--avatar-md-width' },
      lg: { root: 'xs:z--avatar-lg-height xs:z--avatar-lg-width' },
      xl: { root: 'xs:z--avatar-xl-height xs:z--avatar-xl-width' },
    },
    sizeSm: {
      sm: { root: 'sm:z--avatar-sm-height sm:z--avatar-sm-width' },
      md: { root: 'sm:z--avatar-md-height sm:z--avatar-md-width' },
      lg: { root: 'sm:z--avatar-lg-height sm:z--avatar-lg-width' },
      xl: { root: 'sm:z--avatar-xl-height sm:z--avatar-xl-width' },
    },
    sizeMd: {
      sm: { root: 'md:z--avatar-sm-height md:z--avatar-sm-width' },
      md: { root: 'md:z--avatar-md-height md:z--avatar-md-width' },
      lg: { root: 'md:z--avatar-lg-height md:z--avatar-lg-width' },
      xl: { root: 'md:z--avatar-xl-height md:z--avatar-xl-width' },
    },
    sizeLg: {
      sm: { root: 'lg:z--avatar-sm-height lg:z--avatar-sm-width' },
      md: { root: 'lg:z--avatar-md-height lg:z--avatar-md-width' },
      lg: { root: 'lg:z--avatar-lg-height lg:z--avatar-lg-width' },
      xl: { root: 'lg:z--avatar-xl-height lg:z--avatar-xl-width' },
    },
    sizeXl: {
      sm: { root: 'xl:z--avatar-sm-height xl:z--avatar-sm-width' },
      md: { root: 'xl:z--avatar-md-height xl:z--avatar-md-width' },
      lg: { root: 'xl:z--avatar-lg-height xl:z--avatar-lg-width' },
      xl: { root: 'xl:z--avatar-xl-height xl:z--avatar-xl-width' },
    },
    shape: {
      circle: { root: 'z--avatar-circle-radius' },
      rounded: { root: 'z--avatar-rounded-radius' },
    },
    color: {
      neutral: { root: 'bg-neutral-strong-1 text-static-white-main' },
      'static-white': { root: 'bg-static-white-main text-static-black-main' },
      red: { root: 'bg-red-main text-red-main-foreground' },
      orange: { root: 'bg-orange-main text-orange-main-foreground' },
      yellow: { root: 'bg-yellow-main text-yellow-main-foreground' },
      lime: { root: 'bg-lime-main text-lime-main-foreground' },
      green: { root: 'bg-green-main text-green-main-foreground' },
      emerald: { root: 'bg-emerald-main text-emerald-main-foreground' },
      cyan: { root: 'bg-cyan-main text-cyan-main-foreground' },
      blue: { root: 'bg-blue-main text-blue-main-foreground' },
      sky: { root: 'bg-sky-main text-sky-main-foreground' },
      violet: { root: 'bg-violet-main text-violet-main-foreground' },
      fuchsia: { root: 'bg-fuchsia-main text-fuchsia-main-foreground' },
      pink: { root: 'bg-pink-main text-pink-main-foreground' },
      'brand-primary-a': {
        root: 'bg-brand-primary-a-main text-brand-primary-a-main-foreground',
      },
      'brand-primary-b': {
        root: 'bg-brand-primary-b-main text-brand-primary-b-main-foreground',
      },
      'brand-secondary-a': {
        root: 'bg-brand-secondary-a-main text-brand-secondary-a-main-foreground',
      },
      'brand-secondary-b': {
        root: 'bg-brand-secondary-b-main text-brand-secondary-b-main-foreground',
      },
    },
    isInactive: {
      true: {
        root: 'bg-neutral-muted-2 text-neutral-strong-1',
        image: 'opacity-disabled',
      },
    },
    showOutline: {
      true: { root: 'z--avatar-border-width border-solid' },
    },
    outlineColor: {
      neutral: { root: 'border-neutral-strong-1' },
      'static-white': { root: 'border-static-white-main' },
      red: { root: 'border-red-main' },
      orange: { root: 'border-orange-main' },
      yellow: { root: 'border-yellow-main' },
      lime: { root: 'border-lime-main' },
      green: { root: 'border-green-main' },
      emerald: { root: 'border-emerald-main' },
      cyan: { root: 'border-cyan-main' },
      blue: { root: 'border-blue-main' },
      sky: { root: 'border-sky-main' },
      violet: { root: 'border-violet-main' },
      fuchsia: { root: 'border-fuchsia-main' },
      pink: { root: 'border-pink-main' },
      'brand-primary-a': { root: 'border-brand-primary-a-main' },
      'brand-primary-b': { root: 'border-brand-primary-b-main' },
      'brand-secondary-a': { root: 'border-brand-secondary-a-main' },
      'brand-secondary-b': { root: 'border-brand-secondary-b-main' },
    },
  },
  compoundVariants: [
    {
      isInactive: true,
      showOutline: true,
      class: { root: 'border-neutral-muted-2' },
    },
  ],
});

type Variants = VariantProps<typeof avatarStyles>;

type TypographyVariant = 'caption' | 'subtitle2' | 'h6' | 'h5';

const ICON_SIZE_BY_SIZE: Record<
  NonNullable<Variants['size']>,
  'xs' | 'sm' | 'md' | 'lg'
> = {
  sm: 'xs',
  md: 'sm',
  lg: 'md',
  xl: 'lg',
};

const TYPOGRAPHY_VARIANT_BY_SIZE: Record<
  NonNullable<Variants['size']>,
  TypographyVariant
> = {
  sm: 'caption',
  md: 'subtitle2',
  lg: 'h6',
  xl: 'h5',
};

const mapResponsiveSizeToVariant = (
  size: ResponsiveOption<NonNullable<Variants['size']>> | undefined
): ResponsiveOption<TypographyVariant> | undefined => {
  if (size === undefined) {
    return undefined;
  }
  if (typeof size === 'string') {
    return TYPOGRAPHY_VARIANT_BY_SIZE[size];
  }
  return Object.fromEntries(
    Object.entries(size).map(([bp, value]) => [
      bp,
      value ? TYPOGRAPHY_VARIANT_BY_SIZE[value] : value,
    ])
  ) as ResponsiveOption<TypographyVariant>;
};

type AvatarBaseProps = {
  /**
   * Image source. When loaded successfully the avatar renders an `<img>`;
   * on load failure the fallback cascade engages.
   */
  src?: string;
  /**
   * Avatar size. Accepts a static value or a responsive object
   * (e.g. `{ default: 'sm', md: 'lg' }`).
   *
   * @default 'md'
   */
  size?: ResponsiveOption<NonNullable<Variants['size']>>;
  /**
   * Container shape — `circle` rounds to a pill, `rounded` uses a theme-defined radius.
   *
   * @default 'circle'
   */
  shape?: Variants['shape'];
  /**
   * Background colour applied when no image is loaded. Foreground (text /
   * icon) colour is derived to maintain contrast.
   *
   * @default 'neutral'
   */
  color?: Variants['color'];
  /**
   * Semantic "subject is inactive" state. Image content is desaturated.
   *
   * @default false
   */
  isInactive?: boolean;
  /**
   * Render a coloured outline ring around the avatar.
   *
   * @default false
   */
  showOutline?: boolean;
  /**
   * Outline colour. Accepts the same values as `color`. Ignored when
   * `isInactive` is true.
   *
   * @default 'neutral'
   */
  outlineColor?: Variants['outlineColor'];
  /**
   * Icon node rendered when no image is available. Takes precedence over
   * `children`. Sized by the avatar `size`.
   */
  icon?: ReactElement;
  /**
   * Initials text rendered when no image is loaded and no `icon` is
   * provided. Rendered as-is — the consumer is responsible for casing and
   * length.
   */
  children?: ReactNode;
  /**
   * Accessible name for the avatar. When provided the avatar is announced by
   * assistive technology; when absent it is treated as decorative
   * (`aria-hidden`).
   */
  'aria-label'?: string;
};

type AvatarProps = ComponentProps<AvatarBaseProps, 'span'>;

type AvatarComponent = Component<AvatarBaseProps, 'span'>;

const Avatar: AvatarComponent = (props) => {
  const {
    size: sizeProp = 'md',
    shape = 'circle',
    color = 'neutral',
    isInactive = false,
    showOutline = false,
    outlineColor = 'neutral',
    src,
    icon,
    children,
    className,
    ref,
    ...rest
  } = props;

  const { defaultOption: defaultSize, responsiveOption: responsiveSize } =
    resolveResponsiveProps(sizeProp);
  const size = defaultSize ?? 'md';

  const imageStatus = useImageLoadingStatus(src);

  const styles = avatarStyles({
    size,
    sizeXs: responsiveSize?.xs,
    sizeSm: responsiveSize?.sm,
    sizeMd: responsiveSize?.md,
    sizeLg: responsiveSize?.lg,
    sizeXl: responsiveSize?.xl,
    shape,
    color,
    isInactive,
    showOutline,
    outlineColor: showOutline && !isInactive ? outlineColor : undefined,
  });

  const accessibleName = rest['aria-label'];

  let content: ReactElement | null;
  if (src && imageStatus !== 'error') {
    content =
      imageStatus === 'loaded' ? (
        <img src={src} alt="" aria-hidden={true} className={styles.image()} />
      ) : null;
  } else if (icon) {
    content = cloneElement(
      icon as ReactElement<{ size?: string; 'aria-hidden'?: boolean }>,
      {
        size: ICON_SIZE_BY_SIZE[size],
        'aria-hidden': true,
      }
    );
  } else if (children !== undefined && children !== null) {
    content =
      typeof children === 'string' ? (
        <Typography
          component="span"
          variant={mapResponsiveSizeToVariant(sizeProp)}
          aria-hidden="true"
        >
          {children}
        </Typography>
      ) : (
        <span aria-hidden="true">{children}</span>
      );
  } else {
    content = (
      <CoreIcon
        name="person"
        size={ICON_SIZE_BY_SIZE[size]}
        aria-hidden={true}
      />
    );
  }

  return (
    <span
      {...rest}
      ref={ref}
      role="img"
      aria-hidden={accessibleName ? undefined : true}
      aria-label={accessibleName}
      className={styles.root({ className })}
    >
      {content}
    </span>
  );
};

Avatar.displayName = 'Avatar';

export { Avatar, type AvatarProps, type AvatarBaseProps, avatarClasses };
